// Кабинет руководителя: оргструктура и правила «что горит».
//
// Зачем отдельный файл. Экран собирается из готовых хуков, но двух вещей в ядре нет
// в готовом виде: дерева отделов (ядро отдаёт плоский список) и порогов «сделка без
// движения», «согласование зависло». Правила живут здесь, разметка — в pages/Dashboard.tsx.
//
// ГЛАВНОЕ ПРАВИЛО ЭТОГО ФАЙЛА. «Мои люди» и «моя зона ответственности» — ДВА РАЗНЫХ
// ИЗМЕРЕНИЯ, и здесь нет ни одной функции, которая сводила бы их в одно число.
//   • состав отдела  — оргструктура Bitrix (UF_DEPARTMENT + UF_HEAD), ядро отдаёт в /refs;
//   • деньги         — ответственный в сделке/платеже (ASSIGNED_BY_ID, assignedById).
// Связи между ними в Bitrix нет вообще (`ЗНАНИЕ_из_exec_панели.md` §2.1). Живой случай:
// у руководителя сервиса СПб 16 человек в отделе и ноль дебиторки, потому что деньги
// по Петербургу висят на координаторах ЧУЖОГО отдела. Сложи мы эти измерения в одну
// цифру — экран бы врал, и проверить его было бы нечем.
//
// Никаких своих формул по деньгам здесь тоже нет: долг и просрочка считаются в
// lib/payments.ts тем же кодом, что и раздел «Дебиторка».

import type { Deal } from '../api/types'
import { daysPast, isoDay } from './format'
import { toNumber } from './money'

// ─────────────────────────── пороги ───────────────────────────
// Пороги в днях. Все они ПОКАЗАНЫ НА ЭКРАНЕ рядом с цифрой: «горит» — это не свойство
// данных, а наше соглашение, и пользователь должен видеть, какое именно.
// Числа взяты из exec-панели, где они уже прожиты: 30 дней «без движения» — половина
// её порога `stuck` (60), потому что там это была разметка качества за год, а здесь —
// список на сегодня. 30 дней на согласование — то же самое для смарт-процесса 176.

/** Открытая сделка, которую не трогали столько дней, считается «без движения». */
export const STUCK_DAYS = 30
/** Запись согласования (176) без изменений столько дней считается зависшей. */
export const HANG_DAYS = 30
/** Сколько строк показываем в списках «что горит». Полные списки — в своих разделах. */
export const HOT_ROWS = 10
/** Страница ядра. Всё, что «по загруженной странице», ограничено этим числом. */
export const PAGE = 50

// ─────────────────── стадии счетов и согласований: почему они здесь кодами ───────────────────
//
// Обычно мы стадии НЕ зашиваем: у них есть семантика Bitrix (`S` — успех, `F` — провал),
// и её достаточно, чтобы отличить закрытое от открытого. Это работает для платежей (134):
// там `SUCCESS`/`FAIL` расставлены.
//
// У типов 31 и 176 семантика НЕ РАССТАВЛЕНА — проверено на живом ядре, числа на 28.07.2026:
//   • 31 «Счета клиенту» — все 8 стадий имеют semantics='P' («в работе»), включая
//     `DT31_5:P` «Оплачен». То есть «оплачен ли счёт» из семантики не выводится ВООБЩЕ:
//     по ней все 31 467 счетов открыты. (Ровно на это и наступил Client360, пока считал
//     открытые счета по семантике: «оплаченных» у него всегда выходило ноль. Теперь он
//     берёт коды отсюда — чтобы «оплачено» на двух экранах значило одно и то же.)
//   • 176 «Отчёты по сделкам» — semantics='P' у пяти стадий из семи, включая
//     `UC_QC0737` «Отчет сдан», которая занимает 20 462 записи из 24 094. Без её
//     исключения «зависшие согласования» — это весь архив сданных отчётов: у одного
//     координатора выходило 1287 позиций вместо 39, у другого 249 вместо 5.
//     По счетам разница того же порядка: 1013 «просроченных» вместо 137.
//
// Поэтому здесь лежат КОДЫ стадий (короткая часть после двоеточия), а не полные `stageId`:
// код — это идентификатор Bitrix, он не меняется от переименования стадии и одинаков во
// всех воронках типа. Полные `stageId` собираются из справочника `/refs` функцией
// `stageIdsByCode` ниже — то есть добавят воронку, и отбор подхватит её сам.
//
// Правильное лечение — расставить семантику стадий в Bitrix; тогда эти два списка удаляются.
// На экране написано, что отбор идёт по кодам, а не по смыслу.

/** Стадии типа 31, означающие «счёт оплачен». Всё прочее (в т.ч. «Оплачен частично») — долг. */
export const INVOICE_PAID_CODES = ['P']

/**
 * Стадии типа 176, на которых отчёт РЕАЛЬНО ждёт человека: подготовка, проверка у
 * руководства, исправление ошибок, повторная проверка. «Отчет сдан», «Успех» и «Провал»
 * сюда не входят — это конец пути.
 */
export const REPORT_HANG_CODES = ['NEW', 'PREPARATION', 'CLIENT', 'UC_KI5Q0H']

/**
 * Полные `stageId` по коротким кодам — из справочника стадий типа.
 *
 * Зачем прослойка. В фильтр ядра идёт полный `stageId` («DT31_5:P»), а зашивать его нельзя:
 * номер воронки в нём меняется вместе с настройками Bitrix. Справочник знает и код, и полный
 * id, поэтому список собирается из него. Пустой ответ означает «справочник не пришёл» —
 * вызывающий обязан это заметить и сказать пользователю, а не молча показать всё подряд.
 */
export function stageIdsByCode(
  options: Array<{ id: string; code: string }>, codes: string[],
): string[] {
  const want = new Set(codes)
  return options.filter((o) => want.has(o.code)).map((o) => o.id)
}

// ─────────────────────────── оргструктура ───────────────────────────

/**
 * Отдел из `GET /refs` (`refs_departments` в core_api).
 *
 * Тип описан здесь, а не в api/types.ts, намеренно: ядро отдаёт `departments` уже сейчас,
 * а контракт в types.ts правит владелец файла. Как только `RefsData.departments` там
 * появится — этот интерфейс удаляется, а импорт переезжает туда.
 *
 * `users` — только ДЕЙСТВУЮЩИЕ сотрудники: ядро собирает состав по `WHERE u.active`.
 * Уволенные остаются ответственными в старых сделках, но в составе отдела их нет,
 * и на экране это написано словами.
 */
export interface RefDepartment {
  id: number
  name: string | null
  parent_id: number | null
  head_user_id: number | null
  sort: number | null
  deleted: boolean
  users: number[]
}

/** Кусок ответа `/refs`, который нас интересует. Остальное разбирает lib/refs.ts. */
export interface RefsOrgData {
  departments?: RefDepartment[]
}

export interface OrgApi {
  /** false — ядро отделов не отдало (старая версия или структуру ещё не заливали). */
  loaded: boolean
  /** Всего отделов и сколько из них с заполненным руководителем — для честной подписи. */
  total: number
  withHead: number
  get(id: number | string | null | undefined): RefDepartment | undefined
  name(id: number | string | null | undefined): string
  /** Прямые подчинённые отделы. */
  childrenOf(id: number): RefDepartment[]
  /**
   * Отделы, где сотрудник — руководитель, БЕЗ вложенных в другие его же отделы.
   * Иначе поддерево посчиталось бы дважды: у Ильина корень «Связь и Радионавигация»
   * и половина второго уровня одновременно.
   */
  headRoots(userId: string): RefDepartment[]
  /** Отделы, в которых сотрудник ЧИСЛИТСЯ (UF_DEPARTMENT). Их может быть несколько. */
  memberOf(userId: string): RefDepartment[]
  /** Отдел и все подчинённые — обход вниз по parent_id. */
  subtree(rootIds: number[]): RefDepartment[]
  /** Путь от корня до отдела — хлебными крошками, чтобы «СПб» не висел в воздухе. */
  pathOf(id: number): RefDepartment[]
  /** Уникальные id сотрудников перечисленных отделов, строками (в фильтры ядра идут строки). */
  peopleOf(deps: RefDepartment[]): string[]
}

const byOrder = (a: RefDepartment, b: RefDepartment) =>
  (a.sort ?? 0) - (b.sort ?? 0) || (a.name || '').localeCompare(b.name || '', 'ru')

/** Собрать работу с деревом из плоского списка ядра. `undefined` — отделов нет. */
export function makeOrg(data?: RefsOrgData): OrgApi {
  const all = data?.departments ?? []
  // Удалённые отделы ядро отдаёт намеренно: в старых записях ссылки на них остались,
  // и подписать их названием честнее, чем показать номер. Но в СОСТАВ и в поддерево
  // они не идут — иначе «людей в зоне» надуется расформированными отделами.
  const live = all.filter((d) => !d.deleted)
  const byId = new Map<number, RefDepartment>(all.map((d) => [d.id, d]))

  const kids = new Map<number, RefDepartment[]>()
  for (const d of live) {
    if (d.parent_id === null || d.parent_id === undefined) continue
    const list = kids.get(d.parent_id)
    if (list) list.push(d)
    else kids.set(d.parent_id, [d])
  }
  for (const list of kids.values()) list.sort(byOrder)

  const childrenOf = (id: number) => kids.get(id) ?? []

  const subtree = (rootIds: number[]): RefDepartment[] => {
    const seen = new Set<number>()
    const out: RefDepartment[] = []
    const stack = [...rootIds]
    while (stack.length) {
      const id = stack.pop() as number
      if (seen.has(id)) continue
      seen.add(id)
      const d = byId.get(id)
      // Циклов в Bitrix быть не должно, но seen защищает и от них: обход по parent_id
      // с испорченными данными иначе повесил бы браузер намертво.
      if (!d || d.deleted) continue
      out.push(d)
      for (const c of childrenOf(id)) stack.push(c.id)
    }
    return out.sort(byOrder)
  }

  const headRoots = (userId: string): RefDepartment[] => {
    const uid = String(userId)
    const mine = live.filter((d) => d.head_user_id !== null && String(d.head_user_id) === uid)
    const mineIds = new Set(mine.map((d) => d.id))
    // «Корневой» = тот, чей родитель (или родитель родителя…) мной не руководится.
    return mine.filter((d) => {
      let p = d.parent_id
      const guard = new Set<number>()
      while (p !== null && p !== undefined && !guard.has(p)) {
        if (mineIds.has(p)) return false
        guard.add(p)
        p = byId.get(p)?.parent_id ?? null
      }
      return true
    }).sort(byOrder)
  }

  return {
    loaded: all.length > 0,
    total: all.length,
    withHead: all.filter((d) => d.head_user_id !== null && d.head_user_id !== undefined).length,
    get: (id) => (id === null || id === undefined ? undefined : byId.get(Number(id))),
    name: (id) => {
      if (id === null || id === undefined || id === '') return '—'
      const d = byId.get(Number(id))
      if (!d) return `Отдел #${id}`
      return (d.name || `Отдел #${id}`) + (d.deleted ? ' (расформирован)' : '')
    },
    childrenOf,
    headRoots,
    memberOf: (userId) => {
      const uid = Number(userId)
      return live.filter((d) => d.users.includes(uid)).sort(byOrder)
    },
    subtree,
    pathOf: (id) => {
      const out: RefDepartment[] = []
      const guard = new Set<number>()
      let cur: number | null | undefined = id
      while (cur !== null && cur !== undefined && !guard.has(cur)) {
        guard.add(cur)
        const d = byId.get(cur)
        if (!d) break
        out.unshift(d)
        cur = d.parent_id
      }
      return out
    },
    peopleOf: (deps) => {
      const seen = new Set<string>()
      for (const d of deps) for (const u of d.users) seen.add(String(u))
      return [...seen]
    },
  }
}

// ─────────────────────────── деньги по сделкам ───────────────────────────

export interface DealsTotal {
  /** Сумма рублёвых сделок. Сделки без валюты Bitrix считает рублёвыми — они входят сюда. */
  sum: number
  /** Сколько строк вошло в сумму. */
  count: number
  /**
   * Не рублёвые строки: их суммы в `sum` НЕ входят.
   *
   * Почему не сложили. Курса в ядре нет, а «515 000 ₽ + 6 000 € = 521 000» — это уже не
   * деньги, а число. Client360 складывает и предупреждает; здесь на плитке живёт одна
   * цифра, поэтому честнее вынести чужую валюту отдельной строкой, чем испортить главную.
   */
  otherCount: number
  otherCurrencies: string[]
}

/** Итог по загруженным строкам сделок. Пустая сумма — это не ноль, в count она не идёт. */
export function sumDeals(rows: Deal[]): DealsTotal {
  let sum = 0
  let count = 0
  let otherCount = 0
  const other = new Set<string>()
  for (const d of rows) {
    const cur = String(d.CURRENCY_ID || 'RUB')
    const v = toNumber(d.OPPORTUNITY)
    if (cur !== 'RUB') {
      otherCount += 1
      other.add(cur)
      continue
    }
    if (v === null) continue
    sum += v
    count += 1
  }
  return { sum, count, otherCount, otherCurrencies: [...other].sort() }
}

/** Сколько дней сделку не трогали. null — даты изменения нет (такое в Bitrix бывает). */
export function idleDays(d: Deal, today?: string): number | null {
  const day = isoDay(d.DATE_MODIFY)
  return day ? daysPast(day, today) : null
}

/** Сколько дней прошло от значения-даты Bitrix до сегодня. null — не дата. */
export function daysSince(v: unknown, today?: string): number | null {
  const day = isoDay(v)
  return day ? daysPast(day, today) : null
}

/** Сколько строк на каждого ответственного — для разреза «по людям». */
export function countByAssignee(rows: Deal[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const d of rows) {
    const id = String(d.ASSIGNED_BY_ID ?? '')
    if (!id) continue
    m.set(id, (m.get(id) ?? 0) + 1)
  }
  return m
}

// ─────────────────────────── период «за месяц» ───────────────────────────

/**
 * Границы текущего календарного месяца для фильтра ядра.
 *
 * Верхняя граница — первое число СЛЕДУЮЩЕГО месяца со строгим «меньше», а не последнее
 * число текущего: ядро приводит дату без времени к полуночи МСК (`cast_val`), и `<=`
 * по последнему числу отрезало бы всё, что закрыто в этот день после 00:00.
 *
 * Отдельная оговорка про смысл. Здесь календарный месяц по `CLOSEDATE`. В exec-панели
 * «за месяц» считается по ОТЧЁТНОМУ месяцу — паре русских enum-полей (`ЗНАНИЕ_из_exec_панели.md`
 * §3.4), и это другое число. Совпадения с бухгалтерией не ждать; на экране это написано.
 */
export function monthRange(today = new Date()): { from: string; toExcl: string; label: string } {
  const p = (n: number) => String(n).padStart(2, '0')
  const y = today.getFullYear()
  const m = today.getMonth()
  const next = new Date(y, m + 1, 1)
  return {
    from: `${y}-${p(m + 1)}-01`,
    toExcl: `${next.getFullYear()}-${p(next.getMonth() + 1)}-01`,
    // «июль 2026», а не «июль 2026 г.»: подпись плитки читается как «Закрыто за июль 2026»,
    // и «г.» в середине фразы выглядит опечаткой.
    label: today.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
      .replace(/\s*г\.$/, ''),
  }
}

/**
 * Русское склонение существительного после числа: `plural(1,'платёж','платежа','платежей')`.
 *
 * Мелочь, но без неё экран пишет «1 платежей» и «33 человек» — а это первое, обо что
 * спотыкается глаз, и после такого цифрам рядом верят меньше. Тот же приём, что у
 * `fmtDays` в lib/format.ts; вынесен сюда, потому что нужен не только про дни.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b === 1) return one
  if (b >= 2 && b <= 4) return few
  return many
}

/** «Сегодня минус N дней» как ГГГГ-ММ-ДД — граница для фильтров «без движения». */
export function daysAgoIso(days: number, today = new Date()): string {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// ═══════════════════ задания бизнес-процессов: срез кабинета ═══════════════════
//
// Своего расчёта здесь нет ни одного: и «сколько висит», и «сколько дольше порога», и
// «самое старое» уже посчитаны ядром в `/bp/by-user` (bp_api.сводка_по_людям) — тем же
// SQL, каким их показывает раздел «Задания процессов». Здесь только ВЫБОРКА нужных строк
// из общего ответа: мой человек и его подчинённые.
//
// Почему из общего ответа, а не запросом на человека. На 28.07.2026 `/bp/by-user` отдаёт
// ВСЕХ 352 людей одним ответом и без постраничности (так решено в ядре: «разбивать такой
// список на страницы значило бы прятать хвост, в котором сидят уволенные»). Запрос по
// каждому подчинённому дал бы у корня оргструктуры 382 обращения — ровно то, от чего
// стережёт tests/nplus1.test.tsx.

/** Строка `/bp/by-user`. Повторяет `ЧеловекСтрока` из api/bp.ts — без импорта: lib не тянет хуки. */
export interface BpПоЛюдям {
  user_id: number | null
  user_name: string | null
  user_active: boolean | null
  tasks: number
  approve: number
  review: number
  fill: number
  old: number
  overdue: number
  deals: number
  oldest_started: string | null
  oldest_days: number | null
  median_days: number | null
}

/** Итог по группе людей. Все слагаемые — счётчики ядра, а не наши подсчёты по странице. */
export interface BpИтог {
  /** Сколько человек из группы вообще есть в ответе (у остальных живых заданий ноль). */
  people: number
  tasks: number
  old: number
  overdue: number
  /** Заданий на отключённых учётках: их не закроет никто, процесс стоит навсегда. */
  leftTasks: number
  leftPeople: number
  /** Самое старое задание группы, в днях. null — заданий нет. */
  oldestDays: number | null
}

const пустойИтог: BpИтог = {
  people: 0, tasks: 0, old: 0, overdue: 0, leftTasks: 0, leftPeople: 0, oldestDays: null,
}

/** Строка одного человека из ответа `/bp/by-user`. `undefined` — заданий на нём нет вовсе. */
export function bpЧеловек(rows: BpПоЛюдям[] | undefined, userId: string): BpПоЛюдям | undefined {
  if (!rows) return undefined
  return rows.find((r) => String(r.user_id ?? '') === String(userId))
}

/**
 * Сложить строки `/bp/by-user` по списку людей.
 *
 * Складываются ТОЛЬКО счётчики (`tasks`, `old`, `overdue`) — их ядро посчитало по всей
 * выборке, и сумма таких чисел остаётся точной. Медианы здесь нет намеренно: медиану
 * группы из медиан людей не сложить, а показать «медиана 61 день» рядом с чужой цифрой
 * значило бы выдать среднее по средним за факт.
 */
export function bpИтогПоЛюдям(rows: BpПоЛюдям[] | undefined, ids: string[]): BpИтог {
  if (!rows || ids.length === 0) return пустойИтог
  const нужны = new Set(ids.map(String))
  const свои = rows.filter((r) => нужны.has(String(r.user_id ?? '')))
  if (свои.length === 0) return пустойИтог
  const дни = свои.map((r) => r.oldest_days).filter((d): d is number => typeof d === 'number')
  return {
    people: свои.length,
    tasks: свои.reduce((s, r) => s + (r.tasks || 0), 0),
    old: свои.reduce((s, r) => s + (r.old || 0), 0),
    overdue: свои.reduce((s, r) => s + (r.overdue || 0), 0),
    leftTasks: свои.filter((r) => r.user_active === false).reduce((s, r) => s + (r.tasks || 0), 0),
    leftPeople: свои.filter((r) => r.user_active === false).length,
    oldestDays: дни.length ? Math.max(...дни) : null,
  }
}

/** Заданий на человека — для колонки в таблице людей. Ноль означает «в ответе его нет». */
export function bpПоId(rows: BpПоЛюдям[] | undefined): Map<string, BpПоЛюдям> {
  const m = new Map<string, BpПоЛюдям>()
  for (const r of rows ?? []) m.set(String(r.user_id ?? ''), r)
  return m
}

// ═══════════════════ разрывы цепочки: срез кабинета ═══════════════════
//
// Что такое разрыв и как он считается — решает ядро (chain_api.РАЗРЫВЫ и УСЛОВИЕ_РАЗРЫВА).
// Здесь нет ни одного условия вида «есть АВР, нет счёта»: экран берёт готовые коды из поля
// `gaps` каждой строки. Иначе у «работа принята, счёта нет» появилось бы два определения —
// одно в SQL, другое в браузере, — и они разошлись бы на первой же правке.

/**
 * Какие разрывы кабинет выносит на плитки и в каком порядке.
 *
 * Отобраны по вопросу экрана — «что у МЕНЯ встало и стоит денег»:
 *   • no_invoice        — работа принята, счёта нет: деньги не начнут идти вовсе;
 *   • invoice_no_payment— счёт есть, а карточки платежа нет: сделка не попадёт в дебиторку;
 *   • payment_overdue   — срок оплаты прошёл, оплаты нет;
 *   • products_mismatch — состав и сумма сделки разошлись.
 * Остальные десять видов остаются в разделе «Путь сделки»: они либо про чужую зону
 * (закупка, отгрузка в 1С), либо наблюдения, а не остановка работы.
 */
export const КЛЮЧЕВЫЕ_РАЗРЫВЫ = [
  'no_invoice', 'invoice_no_payment', 'payment_overdue', 'products_mismatch',
] as const

/** Строка `/chain/gaps` в том объёме, который нужен кабинету. */
export interface РазрывСтрока {
  deal_id: number
  title: string | null
  opportunity: number | null
  currency: string | null
  company_id: number | null
  gaps: string[]
  stuck_at: { step: string; code: string; title: string } | null
}

/**
 * Сколько строк каждого вида разрыва на ЗАГРУЖЕННОЙ странице.
 *
 * Именно по странице, и на экране это подписано: точное число по видам ядро отдаёт только
 * в `/chain/refs`, а это ещё одно обращение ради четырёх цифр. Общее число сделок с
 * разрывом (`total`) ядро считает по всей выборке — оно точное, и показывается отдельно.
 */
export function разрывыПоВидам(rows: РазрывСтрока[] | undefined): Map<string, number> {
  const m = new Map<string, number>()
  for (const r of rows ?? []) {
    for (const код of r.gaps ?? []) m.set(код, (m.get(код) ?? 0) + 1)
  }
  return m
}

/** Сумма по строкам разрывов — только рубли. Валюту не смешиваем: курса в ядре нет. */
export function разрывыРубли(rows: РазрывСтрока[] | undefined, код?: string): {
  sum: number; count: number; other: number
} {
  let sum = 0
  let count = 0
  let other = 0
  for (const r of rows ?? []) {
    if (код && !(r.gaps ?? []).includes(код)) continue
    if (String(r.currency || 'RUB') !== 'RUB') { other += 1; continue }
    sum += toNumber(r.opportunity) ?? 0
    count += 1
  }
  return { sum, count, other }
}
